import {
  Snackbar,
  Alert,
  CircularProgress,
  Backdrop,
  Icon,
  Card,
  Button,
  Grid,
} from "@mui/material";

import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

// Material Dashboard 2 React components
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";

// Material Dashboard 2 React example components
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";

import axios from "axios.js";
import usePageTitle from "app/pageTitle";

import DataTable from "theme/Tables/DataTable";

function AgentGroupViewPage() {
  const title = "Agent Information";
  usePageTitle(title);

  const { ref } = useParams();
  const navigate = useNavigate();

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  const [pageLoaded, setPageLoaded] = useState(false);
  const [agentGroup, setAgentGroup] = useState({});
  const [agents, setAgents] = useState(0);
  const [campaigns, setCampaigns] = useState([]);

  const campaignsTableColumns = [
    { Header: "Campaign", accessor: "campaign", align: "left" },
    { Header: "Type", accessor: "campaign_type", align: "left" },
    { Header: "Transactions", accessor: "count", align: "right" },
    { Header: "Active Agents", accessor: "agents", align: "right" },
    { Header: "Active Agent Score", accessor: "score", align: "right" },
    { Header: "Campaign Performance", accessor: "overall", align: "right" },
  ];

  const buildCampaignsRows = (data) => {
    const rows = [];
    data.map((row) => {
      rows.push({
        campaign: row.campaign,
        campaign_type: row.campaign_type,
        count: row.count,
        agents: row.agents,
        score: `${(row.score * 100).toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}%`,
        overall: `${(row.overall * 100).toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}%`,
      });
      return null;
    });

    return rows;
  };

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);
      await axios
        .get(`portal/agents/groups/${ref}/insights.json`)
        .then((result) => {
          setShowLoading(false);
          console.log(result.data.data);
          if (result.data.status === "success") {
            setAgentGroup(result.data.data.agent_group);
            setAgents(result.data.data.agents);

            const rowsC = buildCampaignsRows(result.data.data.stats);
            setCampaigns(rowsC);

            setPageLoaded(true);
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");
          setErrMsg(
            `Unable to complete request. ${
              navigator.onLine ? "" : "Make sure you have an active internet connection"
            }`
          );
          setOpen(true);
        });
    };

    fetchData();
  }, []);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
      {pageLoaded && (
        <MDBox mb={5}>
          <MDBox mt={3} mb={1} display="flex" justifyContent="space-between">
            <MDBox>
              <Button
                color="dark"
                size="small"
                startIcon={<Icon>west</Icon>}
                onClick={() => {
                  if (window.history.length > 1) {
                    window.history.back();
                  } else {
                    navigate(`/agent-groups`);
                  }
                }}
              >
                Back
              </Button>
            </MDBox>
          </MDBox>
          <MDBox mt={2}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={8} lg={8}>
                <Card sx={{ height: "100%", marginTop: "0px" }}>
                  <MDBox p={2}>
                    <MDBox mt={1} align="center">
                      <MDTypography mb={1}>
                        <Icon>groups</Icon>
                      </MDTypography>
                      <MDTypography variant="h6" fontWeight="medium">
                        {agentGroup.title}
                      </MDTypography>
                      <MDTypography mt={3} variant="h6" fontWeight="light">
                        {agentGroup.created}
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                </Card>
              </Grid>
              <Grid item xs={12} md={4} lg={4}>
                <Card sx={{ height: "100%", marginTop: "0px" }}>
                  <MDBox align="center" p={2}>
                    <MDTypography mt={1} mb={1}>
                      <Icon>support_agent</Icon>
                    </MDTypography>
                    <MDTypography mt={1} variant="h6" fontSize={28}>{`${agents.toLocaleString(
                      "en-US",
                      {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      }
                    )}`}</MDTypography>
                    <MDTypography variant="h6" fontWeight="light">
                      Agents
                    </MDTypography>
                  </MDBox>
                </Card>
              </Grid>
            </Grid>
          </MDBox>
          <MDBox mt={5}>
            <MDBox>
              <Card sx={{ height: "100%", marginTop: "0px" }}>
                <MDBox>
                  <MDBox p={3}>
                    <MDBox>
                      <MDTypography variant="h6">Campaign Performance</MDTypography>
                    </MDBox>
                  </MDBox>
                  <MDBox>
                    <DataTable
                      table={{ columns: campaignsTableColumns, rows: campaigns }}
                      isSorted
                      entriesPerPage={false}
                      showTotalEntries
                      canSearch={false}
                      buttons
                      noEndBorder
                    />
                  </MDBox>
                </MDBox>
              </Card>
            </MDBox>
          </MDBox>
        </MDBox>
      )}
      <Footer />
    </DashboardLayout>
  );
}

export default AgentGroupViewPage;
